import { computed, makeObservable } from "mobx";
import { inject, observer } from "mobx-react";
import { Component } from "react";
import { type IntlShape, injectIntl } from "react-intl";
import { withRouter, type WithRouterProps } from "nvent-web/utils/withRouter";
import { compose } from "nvent-web/utils/compose";

import { LoadingSection } from "nvent-web/components/LoadingSection/LoadingSection";
import RoomForm from "nvent-web/components/RoomForm";
import { RoomsStore } from "nvent-web/stores/Rooms";
import { type NewRoomFormValues } from "nvent-web/types/NewRoomFormValues";
import { type Room } from "nvent-web/types/Room";

interface EditRoomParams {
  projectId: string;
  roomId: string;
}

interface EditRoomFormInnerProps extends EditRoomParams, WithRouterProps {
  intl: IntlShape;
  rooms: RoomsStore;
}

class EditRoomFormInner extends Component<EditRoomFormInnerProps> {
  constructor(props: EditRoomFormInnerProps) {
    super(props);

    makeObservable(this, {
      projectId: computed,
      roomId: computed,
    });
  }

  get projectId(): number {
    return Number(this.props.params.projectId);
  }

  get roomId(): number {
    return Number(this.props.params.roomId);
  }

  componentDidMount() {
    this.props.rooms.getRoom(this.projectId, this.roomId);
  }

  render() {
    const { roomDetails, areRoomDetailsLoading, isRoomCreating } =
      this.props.rooms;

    if (areRoomDetailsLoading) {
      return <LoadingSection />;
    }

    return <>{roomDetails && this.renderForm(roomDetails, isRoomCreating)}</>;
  }

  private initialValues(room: Room): NewRoomFormValues {
    return {
      name: room.name,
      floorConstruction: room.floorConstruction || null,
      floorFinish: room.floorFinish || null,
      area: room.area,
      installableArea: room.installableArea,
      areaType: room.areaType,
    };
  }

  private renderForm(values: Room, isRoomCreating: boolean) {
    return (
      <RoomForm
        isSubmitting={isRoomCreating}
        onCancel={this.handleCancel}
        initialValues={this.initialValues(values)}
        handleSubmit={this.handleSubmit}
      />
    );
  }

  private handleSubmit = async (formValues: NewRoomFormValues) => {
    const { rooms, navigate } = this.props;

    await rooms.updateRoom(formValues, this.projectId, this.roomId);
    navigate(-1);
  };

  private handleCancel = () => {
    this.props.navigate(-1);
  };
}

const EditRoomForm = compose<Record<string, unknown>>(
  inject("rooms"),
  injectIntl,
  withRouter,
  observer
)(EditRoomFormInner);

export default EditRoomForm;
